import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom';
import { zCoachmark } from '@/shared/constants/zIdxs';

export default function Coachmark({
  selector,
  title,
  body,
  stepIndex,
  stepCount,
  onNext,
  onSkip,
}: {
  selector: string;
  title: string;
  body: string;
  stepIndex: number;
  stepCount: number;
  onNext: () => void;
  onSkip: () => void;
}) {
  // The found anchor + its latest rect, tagged with the selector that found
  // it. Validity is DERIVED (`found.selector === selector`), so a step change
  // can never render the new step's copy over the previous step's highlight —
  // not even for one frame.
  const [found, setFound] = useState<{
    selector: string;
    el: HTMLElement;
    rect: DOMRect;
  } | null>(null);
  const anchor = found?.selector === selector ? found : null;
  const anchorEl = anchor?.el ?? null;

  const arrowRef = useRef<HTMLDivElement>(null);
  const {
    refs,
    floatingStyles,
    isPositioned,
    middlewareData,
    placement: finalPlacement,
  } = useFloating({
    placement: 'bottom',
    middleware: [
      offset(12),
      // This is what puts the card ABOVE bottom-nav anchors on ≤767.
      flip(),
      // crossAxis: when neither side has room (anchor taller than the
      // viewport), slide the card over the anchor instead of off-screen —
      // the buttons must always be reachable.
      shift({ padding: 16, crossAxis: true }),
      // eslint-disable-next-line react-hooks/refs -- floating-ui's arrow() reads .current only inside its middleware fn during computePosition (post-commit), never during render; passing the ref object is the library-documented API for elements that mount after first render
      arrow({ element: arrowRef, padding: 12 }),
    ],
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, () => {
        if (reference instanceof HTMLElement && !reference.isConnected) {
          // The anchored node was replaced (react-query refetch) — reset NOW
          // instead of painting one zero-rect tick at the viewport corner
          // and waiting for the watchdog.
          setFound((f) => (f?.el === reference ? null : f));
          return;
        }
        update();
        if (reference instanceof HTMLElement) {
          const rect = reference.getBoundingClientRect();
          setFound((f) => (f?.el === reference ? { ...f, rect } : f));
        }
      }),
  });

  // Anchor lifecycle. While missing: poll (no give-up cap — an anchor that
  // never appears simply renders nothing; page-level effects terminate dead
  // paths via pathUnavailable). While present: watchdog its connectedness —
  // a react-query refetch can replace the anchored node (e.g. a new market
  // takes index 0 in the feed), and the coach-mark must re-anchor to the
  // replacement instead of tracking a detached element.
  useEffect(() => {
    if (anchorEl) {
      const watchdog = window.setInterval(() => {
        if (!anchorEl.isConnected) setFound(null);
      }, 300);
      return () => window.clearInterval(watchdog);
    }
    let timer = 0;
    const find = () => {
      const el = document.querySelector<HTMLElement>(selector);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        // Seed the rect here: autoUpdate (which keeps it fresh) only starts
        // once the card is mounted, and the card mounts gated on this rect —
        // seeding breaks that circular wait.
        setFound({ selector, el, rect: el.getBoundingClientRect() });
        return;
      }
      timer = window.setTimeout(find, 200);
    };
    find();
    return () => window.clearTimeout(timer);
  }, [selector, anchorEl]);

  useLayoutEffect(() => {
    refs.setReference(anchorEl);
  }, [anchorEl, refs]);

  // #root only: the card (and any Modal) portals to <body>, outside it.
  useEffect(() => {
    if (!anchorEl) return;
    const root = document.getElementById('root');
    if (!root) return;
    root.inert = true;
    return () => {
      root.inert = false;
    };
  }, [anchorEl]);

  // Focus "Next" once the card has its real position (the page behind is
  // inert, so focus must start inside the coach-mark; before isPositioned
  // the card is visibility:hidden and can't take focus). Keyed on the anchor
  // too: isPositioned latches true for the hook's lifetime, and a watchdog
  // re-anchor remounts the button mid-step — focus must come back to it.
  const nextRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (isPositioned && anchorEl) nextRef.current?.focus();
  }, [isPositioned, anchorEl]);

  if (!anchor) return null;

  const { rect } = anchor;
  const pad = 6;
  // Caret sits on the card edge that faces the anchor (the side opposite the
  // final placement side — flip() may have inverted the requested one).
  const caretSide = finalPlacement.startsWith('top') ? 'bottom' : 'top';

  return createPortal(
    <>
      {/* pointer-blocking fallback for browsers without `inert` */}
      <div className="fixed inset-0" style={{ zIndex: zCoachmark }} />
      <div
        className="pointer-events-none fixed rounded-md"
        style={{
          zIndex: zCoachmark,
          top: rect.top - pad,
          left: rect.left - pad,
          width: rect.width + pad * 2,
          height: rect.height + pad * 2,
          boxShadow:
            '0 0 0 9999px color-mix(in srgb, var(--background-main) 75%, transparent)',
        }}
      />
      {/* card (hidden until Floating UI computes its first real position —
          otherwise it would flash at the viewport origin for a frame) */}
      <div
        ref={refs.setFloating}
        style={{
          ...floatingStyles,
          zIndex: zCoachmark,
          maxWidth: 300,
          visibility: isPositioned ? undefined : 'hidden',
        }}
        className="w-[calc(100vw-32px)] rounded-md bg-background-secondary p-xl text-gray-text shadow-xl"
      >
        <div
          ref={arrowRef}
          className="absolute size-3 rotate-45 bg-background-secondary"
          style={{
            left: middlewareData.arrow?.x,
            [caretSide]: -6,
          }}
        />
        <p className="text-body-lg font-medium">{title}</p>
        <p className="mt-s text-body-md text-gray">{body}</p>
        <div className="mt-s flex items-center justify-between gap-s">
          <div className="flex items-center gap-1.5">
            {Array.from({ length: stepCount }, (_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full ${
                  i === stepIndex ? 'w-4 bg-purple-default'
                  : i < stepIndex ? 'w-1.5 bg-purple-default'
                  : 'w-1.5 bg-gray-4'
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-s">
            <button
              type="button"
              onClick={onSkip}
              className="btn-reset h-8 rounded-sm bg-purple-01 px-m text-center text-body-md font-medium whitespace-nowrap text-purple-default"
            >
              Skip tutorial
            </button>
            <button
              ref={nextRef}
              type="button"
              onClick={onNext}
              className="btn-reset h-8 rounded-sm bg-purple-default px-m text-body-md font-medium text-gray-inverse hover:bg-purple-hover"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
